import { CardComponentProps, Variant } from "@~/types/types";
import styles from "./ColourSwatches.module.scss";
import classNames from "classnames/bind";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { useRef, useState } from "react";
import { useProductCardEvent } from "@~/contexts/ProductCardEventContext/ProductCardEventContext.tsx";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";
import { useFeatureFlags } from "@~/flags/flags";
import { QuickViewModal } from "@~/product-card-components/__shared__/QuickViewModal/QuickViewModal";
import { isEligibleForQuickView } from "../../utils/product";

const cx = classNames.bind(styles);

function getUniqueColourVariantsWithSwatches(variants: Variant[]): Variant[] {
  return variants.filter((variant, index, arr) => {
    return (
      "colourSwatch" in variant &&
      arr.findIndex(
        (v: Variant) =>
          v.differentiators?.colour === variant.differentiators?.colour,
      ) === index
    );
  });
}

export function ColourSwatches({
  product,
  configProps,
}: Pick<CardComponentProps, "product" | "configProps">) {
  const { variants } = product;
  const { variant: selectedVariant, setSelectedColour } = useVariant();
  const dispatchEvent = useProductCardEvent();
  const flags = useFeatureFlags();
  const squareColourSwatchesEnabled = Boolean(
    flags.squareColourSwatches?.value,
  );
  const [selectionAnnouncement, setSelectionAnnouncement] = useState("");
  const [showQV, setShowQV] = useState(false);
  const swatchListRef = useRef<HTMLDivElement>(null);

  if (product.isProductSet) {
    return null;
  }

  const colourSwatchVariants = getUniqueColourVariantsWithSwatches(variants);

  const maxSwatchesToDisplay = 4;
  const notDisplayedTotal = colourSwatchVariants.length - maxSwatchesToDisplay;
  const moreOptionsAriaLabel =
    notDisplayedTotal === 1
      ? "there is 1 more colour option available"
      : `there are ${notDisplayedTotal} more colour options available`;

  if (colourSwatchVariants.length < 2) {
    return null;
  }

  const swatchesToDisplay = colourSwatchVariants.slice(0, maxSwatchesToDisplay);

  const handleClick = (colour: string) => {
    setSelectedColour(colour);
    setSelectionAnnouncement(`Selected colour: ${colour}`);
    dispatchEvent(productCardEvents.colourSwatchClicked);
    if (
      configProps?.productLinkOpensQuickView &&
      isEligibleForQuickView(product)
    ) {
      setShowQV(true);
    }
  };

  return (
    <>
      <div
        className={cx(styles.container)}
        data-component="pcl-ColourSwatches"
        ref={swatchListRef}
      >
        <span
          className={styles.visuallyHidden}
          aria-live="polite"
          role="status"
        >
          {selectionAnnouncement}
        </span>
        <div className={styles.list}>
          {swatchesToDisplay.map((variant: Variant) => {
            if (!("colourSwatch" in variant) || !variant.colourSwatch) {
              return null;
            }
            const colour = variant.differentiators?.colour || "";
            const isAvailable =
              Boolean(variant.availability?.availableToOrder) ||
              variant.availability?.unavailableReason === "inStoreOnly";
            const isSelected = variant.id === selectedVariant?.id;
            const buttonLabel = `Colour:${colour}${isAvailable ? "" : " - Out of stock"}`;

            return (
              <button
                type="button"
                key={variant.id}
                aria-label={buttonLabel}
                role="radio"
                aria-checked={isSelected}
                className={cx("button", {
                  "button--unavailable": !isAvailable,
                  "button--selected": isSelected,
                  "button--square": squareColourSwatchesEnabled,
                })}
                onClick={() => {
                  handleClick(colour);
                }}
              >
                <img
                  src={`${variant.colourSwatch}${variant.colourSwatch.includes("?") ? "&" : "?"}wid=18&hei=18&fit=crop&sm=c`}
                  className={cx("image", {
                    "image--square": squareColourSwatchesEnabled,
                  })}
                  alt=""
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
        {notDisplayedTotal > 0 && (
          <a
            data-testid="colour-swatch-more-available-total"
            className={cx("total")}
            href={selectedVariant?.pdpURL.url}
            aria-label={moreOptionsAriaLabel}
          >{`+ ${notDisplayedTotal}`}</a>
        )}
      </div>
      {showQV && (
        <QuickViewModal
          product={product}
          showModal={showQV}
          onClose={() => setShowQV(false)}
          originator="PRODUCT_CARD"
          config={configProps?.qvConfig as string | undefined}
        />
      )}
    </>
  );
}
